// src/components/UploadPanel.js
import React, { useState, useEffect, useRef } from 'react'
import { createClient } from '@supabase/supabase-js'
import { useNavigate } from 'react-router-dom'

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

/** Vertical progress timeline */
function ProgressTimeline({ steps = [] }) {
  const dot = (status) => {
    if (status === 'done') return '✅'
    if (status === 'active') return '⏳'
    if (status === 'error') return '❌'
    return '•'
  }
  const color = (status) => {
    if (status === 'done') return '#16a34a'
    if (status === 'active') return '#2563eb'
    if (status === 'error') return '#dc2626'
    return '#64748b'
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '1rem', padding: '1rem 1.25rem' }}>
      <h3 style={{ margin: '0 0 0.5rem 0' }}>Progress</h3>
      <div style={{ fontSize: '0.9rem', color: '#475569' }}>
        {steps.map((s, idx) => (
          <div key={s.key} style={{ display: 'flex' }}>
            <div style={{ width: 24, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ width: 10, height: 10, borderRadius: 9999, marginTop: 6, background: color(s.status) }} />
              {idx < steps.length - 1 && (
                <div
                  style={{
                    width: 2,
                    flexGrow: 1,
                    background: (s.status === 'done' || s.status === 'active') ? '#c7d2fe' : '#e2e8f0',
                    minHeight: 20
                  }}
                />
              )}
            </div>
            <div style={{ marginLeft: 10, paddingBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, color: color(s.status) }}>
                  {dot(s.status)} {s.label}
                </span>
                {s.status === 'active' && (
                  <span
                    style={{
                      fontSize: 12,
                      padding: '2px 6px',
                      borderRadius: 8,
                      background: '#eff6ff',
                      color: '#1d4ed8',
                      border: '1px solid #bfdbfe'
                    }}
                  >
                    in progress
                  </span>
                )}
                {s.status === 'done' && (
                  <span
                    style={{
                      fontSize: 12,
                      padding: '2px 6px',
                      borderRadius: 8,
                      background: '#ecfdf5',
                      color: '#047857',
                      border: '1px solid #a7f3d0'
                    }}
                  >
                    complete
                  </span>
                )}
                {s.status === 'error' && (
                  <span
                    style={{
                      fontSize: 12,
                      padding: '2px 6px',
                      borderRadius: 8,
                      background: '#fef2f2',
                      color: '#b91c1c',
                      border: '1px solid #fecaca'
                    }}
                  >
                    needs attention
                  </span>
                )}
              </div>
              {s.detail && <div style={{ marginTop: 4, color: '#64748b' }}>{s.detail}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function UploadPanel() {
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [message, setMessage] = useState('')
  const [uploadedRow, setUploadedRow] = useState(null)
  const [unparsedRows, setUnparsedRows] = useState([])
  const [selectedRowIds, setSelectedRowIds] = useState([])
  const [steps, setSteps] = useState([])
  const [redirectIn, setRedirectIn] = useState(null)

  const [duplicateNotice, setDuplicateNotice] = useState(null)
  const [newYearNotice, setNewYearNotice] = useState(null)
  const [newYearContext, setNewYearContext] = useState(null) // { newRowId, previousPlanIds }

  const redirectTimerRef = useRef(null)
  const navigate = useNavigate()
  const fileInputRef = useRef(null)

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

  // --- simplified 4-step timeline wording ---
  const makeSingleUploadSteps = () => ([
    { key: 'upload', label: 'Upload', status: 'pending', detail: '' },
    {
      key: 'redact',
      label: 'Secure Processing',
      status: 'pending',
      detail: 'Preparing file — removing identifiers and extracting plan data…'
    },
    {
      key: 'parse',
      label: 'AI Data Extraction',
      status: 'pending',
      detail: 'Analyzing coverage details and creating structured plan data…'
    },
    { key: 'save', label: 'Ready for Review', status: 'pending', detail: 'Plan data ready for verification.' },
  ])

  const makeMergeSteps = () => ([
    { key: 'select', label: 'Select Files to Merge', status: 'done' },
    { key: 'merge', label: 'Merge Files', status: 'pending' },
    {
      key: 'redact',
      label: 'Secure Processing',
      status: 'pending',
      detail: 'Preparing merged file — removing identifiers and extracting plan data…'
    },
    {
      key: 'parse',
      label: 'AI Data Extraction',
      status: 'pending',
      detail: 'Analyzing coverage details and creating structured plan data…'
    },
    { key: 'save', label: 'Ready for Review', status: 'pending', detail: 'Plan data ready for verification.' },
  ])

  const setStepStatus = (key, status, detail) => {
    setSteps(prev => prev.map(s => (s.key === key ? { ...s, status, detail } : s)))
  }
  const setFirstPendingActive = () => {
    setSteps(prev => {
      const idx = prev.findIndex(s => s.status === 'pending')
      if (idx === -1) return prev
      const next = [...prev]
      next[idx] = { ...next[idx], status: 'active' }
      return next
    })
  }
  const advance = (doneKey) => {
    setStepStatus(doneKey, 'done')
    setFirstPendingActive()
  }
  const errorOut = (key, err) => {
    setStepStatus(key, 'error', typeof err === 'string' ? err : (err?.message || 'Unexpected error'))
  }

  // Redirect helper with countdown
  const startRedirect = (to, seconds = 3) => {
    setStepStatus('save', 'done', `Opening Verify in ${seconds}s…`)
    setRedirectIn(seconds)
    if (redirectTimerRef.current) clearInterval(redirectTimerRef.current)
    redirectTimerRef.current = setInterval(() => {
      setRedirectIn(prev => {
        if (prev === 1) {
          clearInterval(redirectTimerRef.current)
          navigate(to)
          return null
        }
        return prev - 1
      })
    }, 1000)
  }

  useEffect(() => {
    setSteps(makeSingleUploadSteps())
    fetchUnparsedRows()
    return () => {
      if (redirectTimerRef.current) clearInterval(redirectTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchUnparsedRows = async () => {
    const { data, error } = await supabase
      .from('plans_test')
      .select('*')
      .is('parse_result', null)

    if (error) {
      console.error('Error fetching rows:', error)
    } else {
      const validRows = (data || []).filter((row) => /\.(png|jpe?g|webp|pdf)$/i.test(row.original_file_url))
      setUnparsedRows(validRows)
    }
  }

  const handleFileChange = (e) => {
    setFile(e.target.files[0])
    setUploadedRow(null)
    setMessage('')
    setDuplicateNotice(null)
    setNewYearNotice(null)
    setNewYearContext(null)
    setSteps(makeSingleUploadSteps())
    setStepStatus('upload', 'active', 'Preparing upload…')
  }

  const handleUpload = async () => {
    if (!file) {
      setMessage('Please select a file.')
      return
    }

    try {
      setUploading(true)
      setMessage('Uploading…')
      setStepStatus('upload', 'active', 'Sending file to storage')

      const fileName = `${Date.now()}_${file.name}`
      const { error: uploadError } = await supabase.storage.from('documents').upload(fileName, file)
      if (uploadError) throw new Error(uploadError.message)

      // determine last_uploaded_by from current user/profile
      let lastUploadedBy = 'Unknown User'
      try {
        const { data: userData, error: userError } = await supabase.auth.getUser()
        if (!userError && userData?.user) {
          const user = userData.user
          const { data: profile, error: profileError } = await supabase
            .from('users') // public.users
            .select('full_name')
            .eq('id', user.id)
            .maybeSingle()

          if (profileError) console.error('UploadPanel: profile lookup error', profileError)

          lastUploadedBy = profile?.full_name || user.email || 'Unknown User'
        } else if (userError) {
          console.error('UploadPanel: auth.getUser error', userError)
        }
      } catch (lookupErr) {
        console.error('UploadPanel: unexpected error resolving last_uploaded_by', lookupErr)
      }

      const { data: insertData, error: insertError } = await supabase
        .from('plans_test')
        .insert([{ original_file_url: fileName, last_uploaded_by: lastUploadedBy }])
        .select()

      if (insertError) {
        advance('upload')
        errorOut('save', `DB insert failed: ${insertError.message}`)
        setMessage(`Upload succeeded but DB insert failed: ${insertError.message}`)
      } else {
        setUploadedRow(insertData[0])
        await fetchUnparsedRows()
        advance('upload')
        setStepStatus('redact', 'pending', 'Click "Process Document" to begin')
        setMessage('')
      }

      if (fileInputRef.current) fileInputRef.current.value = null
      setFile(null)
    } catch (err) {
      console.error('Upload error:', err)
      errorOut('upload', err)
      setMessage(`Upload failed: ${err.message || err}`)
    } finally {
      setUploading(false)
    }
  }

  const handleAIProcess = async () => {
    if (!uploadedRow) return

    try {
      setStepStatus('redact', 'active', 'Preparing file for parsing (redaction/preprocess)')
      setMessage('Preparing for parsing…')

      const isPdf = /\.pdf$/i.test(uploadedRow.original_file_url)

      if (isPdf) {
        setMessage('Processing file with CuvrIQ AI engine...')
        advance('redact')
        setStepStatus('parse', 'active', 'Analyzing coverage details and creating structured plan data…')

        const response = await fetch('/api/parse-pdf', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rowId: uploadedRow.id, fileName: uploadedRow.original_file_url })
        })
        const result = await response.json()

        if (result.success) {
          const {
            duplicate_status,
            benefit_year,
            previous_year_plan_ids,
            parsedRowId,
          } = result

          if (duplicate_status === 'SAME_YEAR_EXISTS') {
            advance('parse')
            setStepStatus('save', 'done', 'This plan already exists in CuvrIQ for this benefit year.')

            setDuplicateNotice({
              title: 'Plan Already Exists',
              message:
                `This benefit summary matches a plan already stored in CuvrIQ` +
                (benefit_year ? ` for benefit year ${benefit_year}.` : '.') +
                '\n\nYou can search for this plan in the View Plans page or simply ask Riq for coverage details.'
            })

            setUploadedRow(null)
            await fetchUnparsedRows()
            setMessage('')
            return
          }

          if (duplicate_status === 'NEW_YEAR_CANDIDATE') {
            advance('parse')
            setStepStatus('save', 'done', 'New benefit year detected. Awaiting your confirmation.')

            setNewYearNotice({
              title: 'New Benefit Year Detected',
              message:
                `This benefit summary appears to be for a new benefit year for a plan already stored in CuvrIQ` +
                (benefit_year ? ` (benefit year ${benefit_year}).` : '.') +
                '\n\nCuvrIQ will save this as a new plan and link it to the previous version so Riq understands year-to-year changes.\n\nClick the button below to confirm.'
            })

            setNewYearContext({
              newRowId: parsedRowId || uploadedRow.id,
              previousPlanIds: previous_year_plan_ids || []
            })

            await fetchUnparsedRows()
            setUploadedRow(null)
            setMessage('')
            return
          }

          // normal success
          advance('parse')
          setStepStatus('save', 'active', 'Saving parsed data')
          advance('save')
          startRedirect(`/verify/${parsedRowId || uploadedRow.id}`, 3)
          setMessage(`✅ PDF parsing complete!\n${result.deleteMessage || ''}`)
        } else {
          errorOut('parse', result.error || 'PDF processing failed')
          setMessage(`❌ PDF processing failed: ${result.error}`)
        }
      } else {
        setMessage('Processing image(s) with GPT-4 Vision...')

        const fileUrl =
          `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/documents/${uploadedRow.original_file_url}`

        advance('redact')
        setStepStatus('parse', 'active', 'Extracting structured data')

        const response = await fetch('/api/process-document', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileUrl, rowId: uploadedRow.id })
        })
        const result = await response.json()

        if (result.success) {
          advance('parse')
          setStepStatus('save', 'active', 'Saving parsed data')
          advance('save')
          startRedirect(`/verify/${uploadedRow.id}`, 3)
          setMessage(`✅ Image parsing complete!\n${result.deleteMessage || ''}`)
        } else {
          errorOut('parse', result.error || 'Image processing failed')
          setMessage(`❌ Image processing failed: ${result.error}`)
        }
      }
    } catch (err) {
      console.error(err)
      errorOut('parse', err)
      setMessage(`❌ Processing failed: ${err.message || err}`)
    }
  }

  // Confirm handler for NEW_YEAR_CANDIDATE
  const handleConfirmNewYearLink = async () => {
    if (!newYearContext?.newRowId) return

    const newRowId = newYearContext.newRowId
    const previousPlanIds = newYearContext.previousPlanIds || []
    const previousVersionId = previousPlanIds[0] || null

    if (!previousVersionId) {
      setNewYearNotice(null)
      setNewYearContext(null)
      startRedirect(`/verify/${newRowId}`, 3)
      return
    }

    try {
      setMessage('Linking new benefit year to previous plan…')

      const { error } = await supabase
        .from('plans_test')
        .update({ previous_version_id: previousVersionId })
        .eq('id', newRowId)

      if (error) {
        console.error('Error linking new-year plan:', error)
        setMessage(`⚠️ New plan saved, but version linking failed: ${error.message}`)
      } else {
        setMessage('✅ New benefit year linked to previous plan. Redirecting to Verify…')
      }
    } catch (err) {
      console.error('Unexpected error linking new-year plan:', err)
      setMessage(`⚠️ Linking failed: ${err.message || err}`)
    } finally {
      setNewYearNotice(null)
      setNewYearContext(null)
      startRedirect(`/verify/${newRowId}`, 3)
    }
  }

  const handleMergeAndParse = async () => {
    if (selectedRowIds.length < 2) {
      setMessage('Please select at least two rows to merge.')
      return
    }

    try {
      setDuplicateNotice(null)
      setNewYearNotice(null)
      setNewYearContext(null)

      setSteps(makeMergeSteps())
      setStepStatus('merge', 'active', 'Combining files into one document')
      setMessage('Merging selected files and parsing...')

      const response = await fetch('/api/merge-and-parse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rowIds: selectedRowIds })
      })

      const result = await response.json()

      if (!result.success) {
        errorOut('merge', result.error || 'Merge/parse failed')
        setMessage(`❌ Parsing failed: ${result.error}`)
        return
      }

      const {
        duplicate_status,
        benefit_year,
        previous_year_plan_ids,
        parsedRowId,
        newRowId,
      } = result

      // ✅ Post-request animation so it doesn’t “jump” from Merge → All Done
      advance('merge')
      await sleep(250)

      setStepStatus('redact', 'active', 'Preparing merged file — removing identifiers and extracting plan data…')
      await sleep(250)
      advance('redact')

      setStepStatus('parse', 'active', 'Analyzing coverage details and creating structured plan data…')
      await sleep(400)
      advance('parse')

      // SAME_YEAR_EXISTS: backend deleted the rows, so DO NOT navigate
      if (duplicate_status === 'SAME_YEAR_EXISTS') {
        setStepStatus('save', 'done', 'This plan already exists in CuvrIQ for this benefit year.')

        setDuplicateNotice({
          title: 'Plan Already Exists',
          message:
            `This benefit summary matches a plan already stored in CuvrIQ` +
            (benefit_year ? ` for benefit year ${benefit_year}.` : '.') +
            '\n\nYou can search for this plan in the View Plans page or simply ask Riq for coverage details.'
        })

        await fetchUnparsedRows()
        setSelectedRowIds([])
        setMessage('')
        return
      }

      // NEW_YEAR_CANDIDATE: show confirm UI and don’t auto-navigate
      if (duplicate_status === 'NEW_YEAR_CANDIDATE') {
        setStepStatus('save', 'done', 'New benefit year detected. Awaiting your confirmation.')

        setNewYearNotice({
          title: 'New Benefit Year Detected',
          message:
            `This benefit summary appears to be for a new benefit year for a plan already stored in CuvrIQ` +
            (benefit_year ? ` (benefit year ${benefit_year}).` : '.') +
            '\n\nCuvrIQ will save this as a new plan and link it to the previous version so Riq understands year-to-year changes.\n\nClick the button below to confirm.'
        })

        setNewYearContext({
          newRowId: parsedRowId || newRowId || selectedRowIds[0],
          previousPlanIds: previous_year_plan_ids || []
        })

        await fetchUnparsedRows()
        setSelectedRowIds([])
        setMessage('')
        return
      }

      // Normal success
      setStepStatus('save', 'active', 'Finalizing plan and preparing for review…')
      await sleep(250)
      advance('save')

      const idToOpen = parsedRowId || newRowId || selectedRowIds[0]
      startRedirect(`/verify/${idToOpen}`, 3)

      setMessage('✅ Parsing complete!')
      await fetchUnparsedRows()
      setSelectedRowIds([])
    } catch (err) {
      console.error(err)
      errorOut('merge', err)
      setMessage(`❌ Parsing failed: ${err.message || err}`)
    }
  }

  return (
    <div
      style={{
        padding: '2rem',
        maxWidth: 1100,
        margin: '0 auto',
        fontFamily: 'sans-serif',
        display: 'grid',
        gridTemplateColumns: '1fr 320px',
        gap: '1.5rem',
        alignItems: 'start',
      }}
    >
      {/* LEFT */}
      <div>
        <div
          style={{
            border: '2px dashed #94a3b8',
            borderRadius: '1rem',
            padding: '3rem 2rem',
            textAlign: 'center',
            backgroundColor: '#f8fafc',
            marginBottom: '2rem',
          }}
        >
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>📁 Upload Insurance File</h2>
          <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
            Drag and drop a PDF or image file here, or click to browse
          </p>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            style={{
              marginBottom: '1rem',
              border: '1px solid #cbd5e1',
              padding: '0.5rem',
              borderRadius: '0.375rem'
            }}
          />
          <br />
          <button
            onClick={handleUpload}
            disabled={uploading}
            style={{
              backgroundColor: '#0ea5e9',
              color: '#fff',
              padding: '0.6rem 1.2rem',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontWeight: 'bold',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            {uploading ? 'Uploading…' : 'Upload File'}
          </button>
        </div>

        {/* SAME-YEAR duplicate notice */}
        {duplicateNotice && (
          <div
            style={{
              backgroundColor: '#fff7ed',
              border: '1px solid #fdba74',
              padding: '1.2rem',
              borderRadius: '0.75rem',
              marginBottom: '1.5rem',
              color: '#7c2d12'
            }}
          >
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#7c2d12' }}>
              {duplicateNotice.title}
            </h3>
            <p style={{ whiteSpace: 'pre-wrap', marginBottom: '1rem' }}>
              {duplicateNotice.message}
            </p>

            <button
              style={{
                backgroundColor: '#0ea5e9',
                color: '#fff',
                padding: '0.5rem 1rem',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
              onClick={() => {
                setDuplicateNotice(null)
                setUploadedRow(null)
                setMessage('')
                setSteps(makeSingleUploadSteps())
              }}
            >
              OK — Return to Upload
            </button>
          </div>
        )}

        {/* NEW-YEAR candidate notice */}
        {newYearNotice && (
          <div
            style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #facc15',
              padding: '1.2rem',
              borderRadius: '0.75rem',
              marginBottom: '1.5rem',
              color: '#78350f'
            }}
          >
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#78350f' }}>
              {newYearNotice.title}
            </h3>
            <p style={{ whiteSpace: 'pre-wrap', marginBottom: '1rem' }}>
              {newYearNotice.message}
            </p>

            <button
              style={{
                backgroundColor: '#22c55e',
                color: '#fff',
                padding: '0.5rem 1rem',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
              onClick={handleConfirmNewYearLink}
            >
              OK — Save as New Linked Version
            </button>
          </div>
        )}

        {uploadedRow &&
          unparsedRows.filter((row) => row.id === uploadedRow.id).length === 1 &&
          unparsedRows.length === 1 && (
            <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
              <p style={{ color: 'green' }}>
                ✅ Upload complete: {uploadedRow.original_file_url}
              </p>
              <button
                onClick={handleAIProcess}
                style={{
                  backgroundColor: '#10b981',
                  color: '#fff',
                  padding: '0.6rem 1.2rem',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
              >
                Process Document
              </button>
            </div>
          )}

        {unparsedRows.length > 1 && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>🖼️ Unparsed Files</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {unparsedRows.map((row) => (
                <label
                  key={row.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#f1f5f9',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.5rem',
                    cursor: 'pointer'
                  }}
                >
                  <input
                    type="checkbox"
                    value={row.id}
                    checked={selectedRowIds.includes(row.id)}
                    onChange={(e) => {
                      const id = row.id
                      setSelectedRowIds((prev) =>
                        e.target.checked ? [...prev, id] : prev.filter((i) => i !== id)
                      )
                    }}
                  />
                  <span style={{ marginLeft: '0.5rem' }}>{row.original_file_url}</span>
                </label>
              ))}
            </div>
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button
                onClick={handleMergeAndParse}
                style={{
                  backgroundColor: '#6366f1',
                  color: '#fff',
                  padding: '0.6rem 1.2rem',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Merge Selected and Parse
              </button>
            </div>
          </div>
        )}

        {message && (
          <div
            style={{
              backgroundColor: '#e2e8f0',
              padding: '1rem',
              borderRadius: '0.5rem',
              whiteSpace: 'pre-wrap'
            }}
          >
            {message}
          </div>
        )}
      </div>

      {/* RIGHT */}
      <div style={{ position: 'sticky', top: 20 }}>
        <ProgressTimeline steps={steps.length ? steps : makeSingleUploadSteps()} />
        <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
          <div>Legend: ⏳ active • ✅ done • ❌ error</div>
        </div>

        {redirectIn !== null && (
          <div
            style={{
              marginTop: 12,
              padding: '8px 12px',
              borderRadius: 8,
              background: '#ecfeff',
              border: '1px solid #a5f3fc',
              color: '#155e75',
              fontSize: 14,
              fontWeight: 600,
              textAlign: 'center'
            }}
          >
            Opening Verify in {redirectIn}s…
          </div>
        )}
      </div>
    </div>
  )
}

export default UploadPanel
